import React, { useEffect, useState } from "react";
import CustomTable from "@components/CustomTable";
import myStore from "./store";
import { Observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import {
  fetchDynamicFilters,
  transformData,
} from "@helpers/helpers";

import { ZERO_VALUE } from "@utils/constants";
import { getColumnsConfig } from "./columns.constants";
import {
  getFiltersConfig,
  getFilterDisplayValues,
  getSelectedFiltersDisplay,
} from "./filters.constants";
import { NOT_FOUND_INDEX } from "./constants";

// Sub-component imports
import AttributesCell from "./AttributesCell";
import AmountCell from "./AmountCell";
import BusinessDayCell from "./BusinessDayCell";
import InTransitDeadlineCell from "./InTransitDeadlineCell";
import PendingRecordCell from "./PendingRecordCell";

const InTransitListComponent = () => {
  const { t: translate, i18n } = useTranslation();

  const [dynamicFilters, setDynamicFilters] = useState([]);
  useEffect(() => {
    fetchDynamicFilters("InTransitFilters", (newFilters) => {
      setDynamicFilters(newFilters);
    });
  }, []);

  const getDynamicFilters = (key, onlyChildren = false) => {
    return transformData(
      (!!dynamicFilters &&
        !!dynamicFilters.length &&
        dynamicFilters.find((filter) => filter.ColumnName === key)?.Values) ||
        [],
      onlyChildren
    );
  };

  const reconciliationStreamTagDisplays = getDynamicFilters(
    "ReconciliationStreamTag"
  );

  const pinningInitialState = ["pin"];

  // Get columns configuration from constants
  let columns = getColumnsConfig(translate);

  // Map sub-components to their renderer functions
  const cellRendererMap = {
    AttributesCell: ({ row }) => <AttributesCell row={row} />,
    AmountCell: ({ row }) => <AmountCell row={row} />,
    BusinessDayCell: ({ row }) => <BusinessDayCell row={row} />,
    InTransitDeadlineCell: ({ row }) => <InTransitDeadlineCell row={row} />,
    PendingRecordCell: ({ row }) => <PendingRecordCell row={row} />,
  };

  // Replace cellComponent references with actual cell functions
  columns = columns.map((column) => {
    if (column.cellComponent && cellRendererMap[column.cellComponent]) {
      return {
        ...column,
        cell: cellRendererMap[column.cellComponent],
      };
    }
    return column;
  });

  // Handle Attributes column popup content
  const attributesColumnIndex = columns.findIndex(
    (col) => col.accessorKey === "Attributes"
  );
  if (attributesColumnIndex !== NOT_FOUND_INDEX) {
    columns[attributesColumnIndex].popupContent = (row) => {
      const value = row.Attributes;
      return (
        <div className="relative ">
          <div className="flex flex-col min-w-[264px] transition-all bg-primary-dark dark:text-white text-[#777f9e] text-14 rounded-lg px-[2px] py-[8px] mb-3 opacity-100">
            {value &&
              value.map((attr, index) => (
                <div
                  key={index}
                  className="flex flex-row justify-between gap-2 mb-[2px]"
                >
                  <span className="py-[2px] whitespace-nowrap text-xs font-semibold">
                    {attr.Key}
                  </span>
                  <span className="py-[2px] px-[8px] whitespace-nowrap ltr:text-right rtl:text-left text-sm">
                    {attr.Value}
                  </span>
                </div>
              ))}
          </div>
        </div>
      );
    };
  }

  // Get filters configuration from constants
  const filters = getFiltersConfig(translate, getDynamicFilters);

  const filterDisplayValues = getFilterDisplayValues(translate);

  const selectedFiltersDisplay = getSelectedFiltersDisplay(translate);

  return (
    <Observer>
      {() => {
        return (
          <CustomTable
            headerHeight={37}
            store={myStore}
            columnsWithoutPin={columns}
            loading={myStore.loading}
            pinCondition={(row) => {
              return row.IsScheduled;
            }}
            filterDisplayValues={filterDisplayValues}
            selectedFiltersDisplay={selectedFiltersDisplay}
            filters={filters}
            pinningInitialState={pinningInitialState}
            setParentSelectedValues={() => {}}
            actions={{}}
            prefix="InTransit"
            // title={translate('in-transit.title')}
            title={translate("in-transit.title")}
            pinnedRowHeight={57}
            showExportButton={true}
            activityTag="ExportInTransitRecords"
            hideActionColumn={true}
            language={i18n.language}
            maxTableHeight={"calc(100dvh - 340px)"}
            externalDisplays={reconciliationStreamTagDisplays}
          />
        );
      }}
    </Observer>
  );
};

export default InTransitListComponent;
